import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import OperateAside from '@/components/OperateAside';
import { ensureOperateOwner } from '@/utils/operateStorage';

const industries = [
  'Technology', 'Finance & Banking', 'Healthcare', 'Real Estate',
  'Retail & E-Commerce', 'Hospitality & Tourism', 'Manufacturing',
  'Consulting & Professional Services', 'Education', 'Media & Entertainment',
  'Construction', 'Logistics & Supply Chain', 'Other',
];

const MIN_DESC = 20;

function Field({ label, error, delay, children }: {
  label: string; error?: string; delay: number; children: ReactNode;
}) {
  return (
    <div style={{ animation: `opRise .5s ${delay}s both` }}>
      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{label}</label>
      <div className="relative">{children}</div>
      {error && (
        <p className="flex items-center gap-1 text-[12px] font-medium text-red-500 mt-1.5" style={{ animation: 'opFade .25s ease both' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {error}
        </p>
      )}
    </div>
  );
}

export default function OperateCompanyDoing() {
  // Reset stored answers if the logged-in account changed (per-account isolation).
  ensureOperateOwner();
  // Accounting (operate) onboarding — keep the active view on accounting so the logo
  // and the post-onboarding redirect land on the accounting dashboard.
  useEffect(() => { localStorage.setItem('activeServiceView', 'accounting'); }, []);
  const [industry, setIndustry] = useState(() => localStorage.getItem('operateIndustry') || '');
  const [description, setDescription] = useState(() => localStorage.getItem('operateDescription') || '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!industry) e.industry = 'Select your company industry';
    if (!description.trim()) e.description = 'A short business description is required';
    else if (description.trim().length < MIN_DESC) e.description = `Add a little more detail (at least ${MIN_DESC} characters)`;
    return e;
  }, [industry, description]);

  const show = (k: string) => (submitted || touched[k]) ? errors[k] : undefined;
  const blur = (k: string) => setTouched(t => ({ ...t, [k]: true }));

  const handleNext = () => {
    setSubmitted(true);
    if (Object.keys(errors).length) {
      const first = document.querySelector('[data-invalid="true"]') as HTMLElement | null;
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    localStorage.setItem('operateIndustry', industry);
    localStorage.setItem('operateDescription', description.trim());
    navigate('/operate-plan');
  };

  const fieldCls = (k: string) =>
    `w-full px-4 py-3 border rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 transition-all ${
      show(k)
        ? 'border-red-400 focus:ring-red-200 focus:border-red-400'
        : 'border-gray-300 focus:ring-amber-400/40 focus:border-amber-400 hover:border-gray-400'
    }`;

  const descLen = description.trim().length;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <style>{`
        @keyframes opRise { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes opFade { from { opacity:0; } to { opacity:1; } }
        @keyframes opFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
        @keyframes opBarGrow { from { width:0; } to { width:100%; } }
      `}</style>
      <Navbar hideSignBtn={true} logoTo="/dashboard" />
      <div className="flex flex-1">
        <div className="hidden md:block w-1/2 relative">
          <OperateAside
            eyebrow="CONNCCT · ACCOUNTING"
            headline={<>Books that <span style={{ color: '#FFD54F' }}>tell your story.</span></>}
            sub="A chart of accounts and compliance checklist tailored to your industry, so every filing is accurate and on time."
          />
        </div>

        <div className="w-full md:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12 overflow-y-auto">
          <div className="w-full max-w-md mx-auto">
            {/* Progress */}
            <div style={{ animation: 'opFade .5s both' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-amber-600">Step 2 of 2</span>
                <span className="text-[11px] font-semibold text-gray-400">What you do</span>
              </div>
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-7">
                <div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg,#FFD54F,#E6B400)', animation: 'opBarGrow .8s cubic-bezier(.4,0,.2,1) both' }} />
              </div>
              <h1 className="text-[26px] font-extrabold text-gray-900 mb-1.5 leading-tight">What does your company do?</h1>
              <p className="text-sm text-gray-500 mb-7">This helps us tailor your chart of accounts and compliance checklist to your industry.</p>
            </div>

            <div className="space-y-4">
              <div data-invalid={!!show('industry')}>
                <Field label="Company industry" error={show('industry')} delay={0.1}>
                  <select value={industry} onChange={e => setIndustry(e.target.value)} onBlur={() => blur('industry')} className={`${fieldCls('industry')} cursor-pointer appearance-none`}>
                    <option value="">Select your industry</option>
                    {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                </Field>
              </div>

              <div data-invalid={!!show('description')}>
                <Field label="Business description" error={show('description')} delay={0.18}>
                  <textarea
                    placeholder="Briefly describe what your company does, your main products or services, and your customers."
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    onBlur={() => blur('description')}
                    rows={5}
                    maxLength={600}
                    className={`${fieldCls('description')} resize-none`}
                  />
                  <span className={`absolute right-3 bottom-2.5 text-[11px] font-medium ${descLen >= MIN_DESC ? 'text-emerald-500' : 'text-gray-300'}`}>
                    {descLen}/{600}
                  </span>
                </Field>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-8" style={{ animation: 'opRise .5s .28s both' }}>
              <button type="button" onClick={() => navigate(-1)}
                className="px-7 py-3 border-2 border-gray-200 rounded-xl font-semibold text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors">
                ← Back
              </button>
              <button type="button" onClick={handleNext}
                className="flex-1 px-7 py-3 rounded-xl font-bold text-[#17263A] transition-transform hover:-translate-y-0.5 active:translate-y-0 shadow-md"
                style={{ background: 'linear-gradient(135deg,#FFD54F,#E6B400)', boxShadow: '0 6px 18px rgba(230,180,0,.32)' }}>
                Continue →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
